import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {isDefaultTenant, tenantAppDownloadLinks} from '../tenant/tenantState'

const qrUrl = (url: string, size: number = 220) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`

type StoreCardProps = {
  href: string
  qrSrc: string
  qrAlt: string
  qrFallback?: string
  badgeSrc: string
  badgeAlt: string
  badgeStyle: React.CSSProperties
}

const StoreCard: React.FC<StoreCardProps> = ({href, qrSrc, qrAlt, qrFallback, badgeSrc, badgeAlt, badgeStyle}) => {
  const [hover, setHover] = useState(false)
  return (
    <div className='col-12 col-md-5'>
      <div className='card shadow-sm h-100'>
        <div className='card-body d-flex flex-column align-items-center p-8'>
          <img
            src={qrSrc}
            alt={qrAlt}
            width={220}
            height={220}
            style={{borderRadius: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.08)'}}
            onError={
              qrFallback
                ? (e) => {
                    const img = e.currentTarget as HTMLImageElement
                    img.onerror = null
                    img.src = qrFallback
                  }
                : undefined
            }
          />
          <a
            href={href}
            target='_blank'
            rel='noreferrer'
            className='mt-6'
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              display: 'inline-block',
              padding: 0,
              background: 'transparent',
              borderRadius: 0,
              lineHeight: 0,
              boxShadow: 'none',
              transform: hover ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            <img src={badgeSrc} alt={badgeAlt} style={badgeStyle} />
          </a>
        </div>
      </div>
    </div>
  )
}

const DownloadAppPage: React.FC = () => {
  const {formatMessage} = useIntl()
  // Per-tenant store links. A white-label tenant that hasn't published a supplier app yet gets `null`
  // for that store, so we hide the button rather than pointing its suppliers at Dumyah's app.
  const {ios, android} = tenantAppDownloadLinks()

  return (
    <div className='container py-10'>
      <div className='text-center mb-8'>
        <h2 className='fw-bold'>{formatMessage({id: 'download_app'})}</h2>
        <div className='text-muted'>{formatMessage({id: 'download_app_subtitle'})}</div>
      </div>

      {ios || android ? (
        <div className='row g-10 justify-content-center'>
          {ios && (
            <StoreCard
              href={ios}
              qrSrc={qrUrl(ios)}
              qrAlt='iOS App QR'
              badgeSrc='https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83'
              badgeAlt={formatMessage({id: 'download_on_app_store'})}
              badgeStyle={{height: 48, display: 'block'}}
            />
          )}
          {android && (
            <StoreCard
              href={android}
              // The pre-rendered static QR encodes Dumyah's own Play link — only reuse it for Dumyah;
              // every other tenant gets a dynamic QR of its own link (never Dumyah's).
              qrSrc={isDefaultTenant() ? '/media/qr/google-play-qr.png' : qrUrl(android)}
              qrAlt='Android App QR'
              qrFallback={qrUrl(android)}
              badgeSrc='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
              badgeAlt={formatMessage({id: 'get_it_on_google_play'})}
              badgeStyle={{height: 70, display: 'block', marginTop: -10}}
            />
          )}
        </div>
      ) : (
        <div className='text-center text-muted fs-5 py-10'>
          {formatMessage({id: 'download_app_unavailable'})}
        </div>
      )}
    </div>
  )
}

export default DownloadAppPage
